// @flow

import React, { PureComponent } from "react";
import { get, noop, trim } from "lodash";
import { differenceInMinutes } from "date-fns";
import type { RacePanelLink } from "@fdr/types/Race";
import type { TernaryFn, NullaryFn } from "@tvg/types/Functional";
import type { UserOptedInPromos } from "@tvg/types/User";
import { hasPromoFromGraph, getRDAOptinStatus } from "@fdr/utils/PromoUtils";
import Icon from "@fdr/static-ui/Icons";
import { arrowRight, videoReplay, promo } from "@fdr/static-ui/Icons/icons";

import { FDTVLogo } from "@fdr/static-ui/Logos";
import buildColor from "@fdr/static-ui/ColorPalette";
import Flag from "@fdr/static-ui/Flags";
import { getTrackDescription } from "@fdr/utils/raceUtils";

import MTP, { getMTPStatus } from "../../_atoms/MTP";
import {
  PanelContainer,
  PanelWrapper,
  TrackName,
  TagsWrapper,
  InformationWrapper,
  MetaDescript,
  TrackNameWrapper,
  RaceNumberWrapper,
  RaceTitleWrapper,
  FooterWrapper,
  FlagWrapper,
  TagIconsWrapper,
  TagIcon,
  LocationName,
  LogoContainer,
  TitleWrapper,
  TrackTag
} from "./styled-components";
import { getMTPColor } from "../../_atoms/MTP/styled-components";
import { VideoStatus } from "./constants";

type Props = {
  race: RacePanelLink,
  raceCardUrl: string,
  optedInPromos: UserOptedInPromos,
  context: "topRaces" | "upcomingRaces",
  onCardClick: TernaryFn<
    RacePanelLink,
    string,
    string | UserOptedInPromos,
    void
  >,
  renderRunnerList: NullaryFn<mixed>,
  isMTPInline: boolean,
  forceMTPDescription: boolean,
  showActiveBets: boolean,
  useIsPromoTagShownFlag: boolean,
  trackTagText: string
};

const getTrackName = (trackName, isNationalTrack) => {
  const matchString = " - ";

  if (!isNationalTrack && trackName.includes(matchString)) {
    const name = trackName.split(matchString);

    if (name[0].length < 4) {
      return name.length > 2 ? name.slice(1).join(matchString) : name[1];
    }
  }

  return trackName;
};

export default class RaceInfoPanel extends PureComponent<Props> {
  static defaultProps = {
    race: {
      tvgRaceId: "",
      country: "",
      isGreyhound: false,
      mtp: 0,
      raceId: 0,
      trackName: "",
      number: "",
      promos: null,
      talentPicks: [],
      postTime: "",
      trackCode: "",
      highlighted: null
    },
    onCardClick: noop,
    raceCardUrl: "",
    optedInPromos: {},
    context: "topRaces",
    renderRunnerList: noop,
    isMTPInline: false,
    forceMTPDescription: false,
    showActiveBets: false,
    useIsPromoTagShownFlag: false,
    isHighlighted: false,
    trackTagText: ""
  };

  getVideoStatus = () => {
    const { race } = this.props;
    let videoStatus = VideoStatus.UNAVAILABLE;
    if (get(race, "video.liveStreaming")) {
      const dateNow = new Date();
      const raceDate = new Date(get(race, "postTime"));
      const raceDiff = differenceInMinutes(dateNow, raceDate);
      videoStatus =
        raceDiff > -30 ? VideoStatus.STARTED : VideoStatus.START_SOON;
    }

    return videoStatus;
  };

  renderRaceTitleWrapper = (trackName: string, raceNumber: string = "") => (
    <RaceTitleWrapper
      data-qa-label={`race-trackName${
        this.props.race.highlighted ? "_highlighted" : ""
      }`}
    >
      <TrackNameWrapper>{trackName}</TrackNameWrapper>
      <RaceNumberWrapper>&nbsp;{`R${raceNumber}`}</RaceNumberWrapper>
    </RaceTitleWrapper>
  );

  renderRaceInformation = (isHighlighted: boolean) => {
    const { country, raceNumber, trackName, highlighted } = this.props.race;
    const hasCountry = country !== "US" && country !== "USA";
    const highlightedStyle = get(highlighted, "style", "");
    const highlightedDescription = get(highlighted, "description", "");
    const raceDescript = this.renderRaceTitleWrapper(
      getTrackName(trackName, !hasCountry),
      raceNumber
    );
    const hasPromos = hasPromoFromGraph(
      this.props.race,
      this.props.useIsPromoTagShownFlag
    );
    const trackDescription = getTrackDescription(
      trackName,
      get(this.props.race, "track.trackLocation"),
      highlighted
    );
    const trackNameFormatted = trackDescription.trackTitle;

    return (
      <InformationWrapper
        data-qa-label="race-trackInformation"
        to={this.props.raceCardUrl}
        onClick={() =>
          this.props.onCardClick(
            this.props.race,
            this.props.raceCardUrl,
            this.props.context === "topRaces"
              ? "Header"
              : this.props.optedInPromos
          )
        }
        $isHighlighted={isHighlighted ? 1 : 0}
      >
        <TitleWrapper>
          <TrackName
            highlightedStyle={highlightedStyle}
            isHighlighted={isHighlighted}
          >
            {highlightedDescription && highlightedStyle === "Highlighted"
              ? highlightedDescription
              : trackNameFormatted}
          </TrackName>
          {isHighlighted && this.props.trackTagText && (
            <TrackTag isHighlighted={isHighlighted} data-qa-label="track-tag">
              {this.props.trackTagText}
            </TrackTag>
          )}
        </TitleWrapper>
        <MetaDescript
          highlightedStyle={highlightedStyle}
          data-qa-label="meta-description"
          isHighlighted={isHighlighted}
        >
          {highlightedDescription && highlightedStyle === "Highlighted"
            ? raceDescript
            : `Race ${get(this.props.race, "raceNumber", "")}`}
        </MetaDescript>
        {this.renderTags(isHighlighted, hasPromos)}
      </InformationWrapper>
    );
  };

  renderTags = (isHighlighted: boolean, hasPromos: boolean) => {
    const videoStatus = this.getVideoStatus();
    const location = get(this.props.race, "track.trackLocation", {});
    const isNationalRace = location.country === "USA";
    const promoType =
      hasPromos && (getRDAOptinStatus(this.props.race) ? "optIn" : "promo");
    const hasFDTVLogo =
      get(this.props.race, "video.onTvg") ||
      get(this.props.race, "video.onTvg2");

    const iconSize = 16;
    const iconColor = isHighlighted
      ? buildColor("white")
      : buildColor("grey", "D3");
    const promosIconColor = isHighlighted
      ? buildColor("yellow")
      : buildColor("grey", "D3");

    return (
      <TagsWrapper data-qa-label="race-tagsArea">
        <FlagWrapper>
          <Flag country={location.country} circular />
          <LocationName isHighlighted={isHighlighted}>
            {trim(isNationalRace ? location.state : location.country)}
          </LocationName>
        </FlagWrapper>
        <TagIconsWrapper>
          {promoType && this.props.renderRunnerList() && (
            <TagIcon size={iconSize} type={promoType}>
              <Icon
                qaLabel={`race-${promoType}`}
                icon={promo}
                size={iconSize}
                color={promosIconColor}
              />
            </TagIcon>
          )}
          {videoStatus !== VideoStatus.UNAVAILABLE &&
            this.props.renderRunnerList() && (
              <TagIcon size={iconSize} type="video">
                <Icon icon={videoReplay} size={iconSize} color={iconColor} />
              </TagIcon>
            )}
          {hasFDTVLogo && (
            <LogoContainer>{FDTVLogo(iconSize, 47, iconColor)}</LogoContainer>
          )}
        </TagIconsWrapper>
      </TagsWrapper>
    );
  };

  renderFooter = (isHighlighted: boolean) => {
    const mtp = get(this.props, "race.mtp");
    const mtpMode = "light";

    return (
      <FooterWrapper
        $isInline={this.props.isMTPInline}
        ishighlighted={isHighlighted ? 1 : 0}
        to={this.props.raceCardUrl}
        onClick={() => {
          this.props.onCardClick(
            this.props.race,
            this.props.raceCardUrl,
            "Footer"
          );
        }}
      >
        <MTP
          isPlainMTP
          mtp={mtp}
          postTime={get(this.props, "race.postTime")}
          isSmall
          isNewLayout
          mode={mtpMode}
          inline={this.props.isMTPInline}
          forceDescription={this.props.forceMTPDescription}
        />
        {!isHighlighted && (
          <Icon
            icon={arrowRight}
            color={getMTPColor(
              getMTPStatus(get(this.props, "race.status.code"), mtp),
              mtpMode
            )}
            size={17}
            qaLabel="upcomingRacesBetNowIcon"
          />
        )}
      </FooterWrapper>
    );
  };

  render() {
    const { race, renderRunnerList } = this.props;
    const isHighlighted =
      (get(race, "highlighted.style", "") || "").toLowerCase() ===
        "highlighted" && this.props.context === "topRaces";

    return (
      <PanelWrapper
        data-qa-label="races-headerWrapper"
        isHighlighted={isHighlighted}
      >
        <PanelContainer data-qa-label="race-header">
          {this.renderRaceInformation(isHighlighted)}
          {typeof renderRunnerList === "function" && renderRunnerList()}
          {this.renderFooter(isHighlighted)}
        </PanelContainer>
      </PanelWrapper>
    );
  }
}
